import { useCallback, useRef, useState } from "react";
import findAddressByCep, { CepAddressType } from "../resources/Api/location/findAddressByCep";
import useDirectdLookup from "./useDirectdLookup";

type NamedItem = { name: string; acronym: string };

type UseCepAutofillParams = {
    cep: string;
    /** false fora da inclusao/edicao. */
    enabled: boolean;
    /** CEP ja salvo no cadastro: na edicao, abrir o registro nao dispara consulta. */
    savedCep?: string | null;
    inEdit: boolean;
    /** Recebe logradouro/bairro; cada tela decide como montar o campo de endereco. */
    onAddress: (address: CepAddressType) => void;
    /** Telas com pais/estado/cidade. Sem isso, so o endereco e preenchido. */
    location?: {
        countriesList: Array<NamedItem>;
        statesList: Array<NamedItem>;
        citiesList: Array<{ name: string }>;
        country: string;
        state: string;
        setCountry: (name: string) => void;
        setState: (name: string) => void;
        setCity: (name: string) => void;
    };
};

const digits = (raw: string) => raw.replace(/\D/g, "");
const isValidCep = (cep: string) => /^\d{8}$/.test(cep);
const normalizeName = (value: string) =>
    value.normalize("NFD").replace(/\p{M}+/gu, "").toUpperCase().trim();

/**
 * Autocomplete de endereco pelo CEP (ViaCEP, gratuito): consulta no onBlur e apos uma pausa na digitacao.
 *
 * Estado e cidade dependem de listas carregadas em cascata (pais -> estados -> cidades), e os carregadores das
 * telas voltam o estado/cidade para o do registro salvo. Por isso o estado/cidade do CEP fica pendente e as telas
 * chamam [pendingStateName]/[pendingCityName] dentro dos carregadores, depois que a lista chega.
 */
const useCepAutofill = ({ cep, enabled, savedCep, inEdit, onAddress, location }: UseCepAutofillParams) => {
    const pending = useRef<{ uf: string; city: string } | null>(null);
    const [blurredValue, setBlurredValue] = useState<string>("");

    const latestLocation = useRef(location);
    latestLocation.current = location;

    const applyLocation = (address: CepAddressType) => {
        const loc = latestLocation.current;
        if (!loc || !address.state) return;
        pending.current = { uf: address.state.toUpperCase(), city: address.city };

        const brazil = loc.countriesList.find((item) => item.acronym === "BRA");
        if (brazil && loc.country !== brazil.name) {
            // Estados e cidades recarregam e os carregadores aplicam o pendente.
            loc.setCountry(brazil.name);
            return;
        }
        const state = loc.statesList.find((item) => item.acronym === pending.current?.uf);
        if (!state) return;
        if (loc.state !== state.name) {
            // Cidades recarregam e o carregador aplica a cidade pendente.
            loc.setState(state.name);
            return;
        }
        // Mesmo estado: a lista de cidades ja esta carregada.
        const city = loc.citiesList.find((item) => normalizeName(item.name) === normalizeName(address.city));
        if (city) loc.setCity(city.name);
        pending.current = null;
    };

    const lookup = useDirectdLookup<CepAddressType>({
        rawValue: cep,
        normalize: digits,
        isValid: isValidCep,
        enabled,
        shouldSkip: (value) => inEdit && value === digits(savedCep ?? ""),
        fetcher: findAddressByCep,
        onResult: (address) => {
            onAddress(address);
            applyLocation(address);
        },
    });

    /** Nome do estado do CEP na lista recem-carregada, ou undefined se nao houver CEP pendente. */
    const pendingStateName = useCallback((states: Array<NamedItem>) => {
        const uf = pending.current?.uf;
        return uf ? states.find((item) => item.acronym === uf)?.name : undefined;
    }, []);

    /** Nome da cidade do CEP na lista recem-carregada; consome o pendente. */
    const pendingCityName = useCallback((cities: Array<{ name: string }>) => {
        const city = pending.current?.city;
        if (!city) return undefined;
        pending.current = null;
        return cities.find((item) => normalizeName(item.name) === normalizeName(city))?.name;
    }, []);

    const cepDigits = digits(cep);
    const isIncompleteAfterBlur =
        enabled && cepDigits.length > 0 && !isValidCep(cepDigits) && blurredValue === cepDigits;
    const hasError = isIncompleteAfterBlur || (!lookup.loading && !!lookup.error);

    const { lookupNow } = lookup;
    const onBlur = useCallback(() => {
        setBlurredValue(cepDigits);
        lookupNow();
    }, [cepDigits, lookupNow]);

    const helperText = lookup.loading
        ? "Buscando endereço..."
        : isIncompleteAfterBlur
            ? "CEP inválido."
            : lookup.error ?? undefined;

    return { onBlur, hasError, helperText, pendingStateName, pendingCityName };
};

export default useCepAutofill;
